import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Center, Divider, Stack, Text } from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AddButtonLink, Content, TextInputControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { PatentFoundModal } from '../Patent/PatentFoundModal';
import { useReclaimAuthorship } from '../Patent/model/PatentQuery';
import { PatentReclaimSearch, ReclaimRequest } from '../Patent/model/PatentTypes';
import { useSearchPatentByIdentifiers } from './model/PatentQuery';
import { PatentSearchRequest } from './model/PatentTypes';

const schema = z.object({
  title: z.string().optional(),
  registrationNo: z.string().optional(),
});

const defaultValues = {
  title: '',
  registrationNo: '',
};

interface PatentSearchClaimableProps {
  useSearchPatentHook?: () => UseMutationResult<PatentReclaimSearch, Error, PatentSearchRequest, unknown>;
}

export function PatentSearchClaimable(props: PatentSearchClaimableProps) {
  const { useSearchPatentHook = useSearchPatentByIdentifiers } = props;

  const form = useForm<PatentSearchRequest>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onSubmit',
  });
  const { t } = useTranslation();

  const { mutate, data, isPending, reset } = useSearchPatentHook();

  const notification = useAppNotification();
  const onSubmit = (values: PatentSearchRequest) => {
    // Only submit if at least one field has a value
    const hasValue = values.title?.trim() || values.registrationNo?.trim();
    if (hasValue) {
      mutate(values, {
        onError: notification.error,
        onSuccess: notification.success,
      });
    }
  };

  const reclaimMutation = useReclaimAuthorship();
  const isSubmitting = isPending || reclaimMutation.isPending;

  const navigate = useNavigate();
  const onReclaimAuthorship = (request: ReclaimRequest) => {
    reset();
    reclaimMutation.mutate(request, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        navigate({ to: `../${request.patentId}` });
      },
    });
  };

  return (
    <Content type="full">
      <Text p="md">{t('patents.searchInfo')}</Text>
      {data && 'patent' in data && (
        <PatentFoundModal
          close={reset}
          data={data}
          onReclaimAuthorship={onReclaimAuthorship}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap="xs">
            <TextInputControl<PatentSearchRequest>
              name="title"
              leftSectionWidth="60px"
              leftSection={<Text fw="500">Title:</Text>}
              label={t('patents.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<PatentSearchRequest>
              name="registrationNo"
              leftSectionWidth="120px"
              leftSection={<Text fw="500">Register No:</Text>}
              label={t('patents.form.fields.registrationNo')}
              isSubmitting={isSubmitting}
            />
            <Center>
              <Button
                loading={isSubmitting}
                type="submit"
              >
                {t('button.search')}
              </Button>
            </Center>
          </Stack>
        </form>
      </FormProvider>
      <Divider />
      <Text
        c="dimmed"
        fs="italic"
        mt="10px"
      >
        {t('patents.addNewDisclaimer')}
      </Text>
      {!isSubmitting && (
        <AddButtonLink
          to="../create"
          name={t('button.addNew')}
        />
      )}
    </Content>
  );
}
